// detailPrimitives.jsx — the small presentational pieces every dense detail
// surface repeats: a labelled field, a titled section, and a chip.
//
// These existed three times over (private in EndpointInventoryPage's drawer,
// hand-copied into EndpointConfigStatePanel, and a third Chip in
// SignalDefinitionPanel / EndpointSignalDetailBody). Copies drift — one gained a
// `mono` variant the others didn't — so they live here instead. Purely
// presentational: no fetching, no store reads.

import { NOTE_CLASS } from "../lib/ui.js";

// A labelled value. Falls back to an em-dash so an empty field reads as
// "known to be absent" rather than as a rendering bug.
export function DetailField({ label, value, mono = false }) {
    return (
        // `content-start`: without it the implicit rows stretch to match a taller
        // neighbour in the same grid row, floating the value away from its label.
        <div className="grid gap-[2px] min-w-0 content-start">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>{label}</span>
            <span
                // `break-all` on mono values, not `break-words`: these are
                // filesystem paths and hashes with no spaces to break at, so
                // word-boundary wrapping leaves them wider than the column and
                // pushes the whole grid into horizontal overflow.
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] ${mono ? "font-mono break-all" : "break-words"}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}

// A titled block. `columns` defaults to the two-up field grid the endpoint
// drawer uses; pass 1 for prose or a list that shouldn't be split into columns.
// `note` carries the section's empty/loading/error line so callers don't each
// invent a placement for it.
export function DetailSection({ title, note, columns = 2, busy = false, children }) {
    return (
        <div className="grid gap-[8px] min-w-0" aria-busy={busy || undefined}>
            {title ? (
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                    {title}
                </span>
            ) : null}
            {note ? <span className={NOTE_CLASS}>{note}</span> : null}
            {children ? (
                // `min-w-0` is load-bearing: a grid item defaults to
                // min-width:auto, so one long unbreakable value can widen the
                // track past the container and clip every sibling at the panel
                // edge instead of wrapping.
                <div
                    className="grid gap-[10px] min-w-0"
                    style={columns > 1 ? { gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))` } : undefined}
                >
                    {children}
                </div>
            ) : null}
        </div>
    );
}

export function Chip({ children, title }) {
    return (
        <span
            title={title}
            className="border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted-soft)]"
        >
            {children}
        </span>
    );
}

// A horizontal rule between detail sections — the bare bordered div the endpoint
// drawer uses between Identity / Agent / Hardware.
export function DetailRule() {
    return <div className="border-t border-[color:var(--line)]" />;
}
